import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { FIN_HELPER_API_BASE } from '../config';
import './PricingHistory.css';

const BRAND_COLOR = '#3e3dc5';

export default function PricingHistory() {
  const { token, logout } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('milka');
  const [query, setQuery] = useState('milka');
  const [selectedStore, setSelectedStore] = useState(null);

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${FIN_HELPER_API_BASE}/search/items/?item_name=${encodeURIComponent(query)}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const items = res.data.items
          .map((item) => ({
            ...item,
            date_bought: item.date_bought.split(' ')[0],
          }))
          .sort((a, b) => {
            const [da, ma, ya] = a.date_bought.split('/');
            const [db, mb, yb] = b.date_bought.split('/');
            return new Date(`${ya}-${ma}-${da}`) - new Date(`${yb}-${mb}-${db}`);
          });
        setData(items);
        setSelectedStore(null);
      })
      .catch(() => {
        setError('Failed to fetch data');
      })
      .finally(() => setLoading(false));
  }, [query, token]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setQuery(search);
  };

  const chartData = selectedStore
    ? data.filter((item) => item.store_name === selectedStore)
    : data;

  return (
    <div className="pricing-history">
      <header className="pricing-history__header" style={{ background: BRAND_COLOR }}>
        <h1 className="pricing-history__title">MONETA Pricing History</h1>
        <button
          type="button"
          className="pricing-history__logout"
          onClick={logout}
        >
          Log out
        </button>
      </header>
      <main className="pricing-history__main">
        <section className="pricing-history__sidebar">
          <form onSubmit={handleSubmit} className="pricing-history__form">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search item name..."
              className="pricing-history__input"
              style={{
                borderColor: BRAND_COLOR,
                boxShadow: `0 0 0 2px ${BRAND_COLOR}22`,
              }}
            />
            <button type="submit" className="pricing-history__btn" style={{ background: BRAND_COLOR }}>
              Search
            </button>
          </form>
          <div className="pricing-history__table-wrap">
            {loading && <p className="pricing-history__msg">Loading...</p>}
            {error && <p className="pricing-history__msg pricing-history__msg--error">{error}</p>}
            {!loading && !error && data.length === 0 && (
              <p className="pricing-history__msg">No data found.</p>
            )}
            {!loading && !error && data.length > 0 && (
              <table className="pricing-history__table">
                <thead>
                  <tr style={{ background: '#e7e7fa' }}>
                    <th>Item Name</th>
                    <th>Price</th>
                    <th>Date</th>
                    <th>Store</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, idx) => (
                    <tr key={idx} className="pricing-history__row">
                      <td>{item.item_name}</td>
                      <td>{item.item_price}</td>
                      <td>{item.date_bought}</td>
                      <td
                        className={`pricing-history__store-cell ${selectedStore === item.store_name ? 'pricing-history__store-cell--active' : ''}`}
                        style={
                          selectedStore === item.store_name
                            ? { background: BRAND_COLOR, color: 'white' }
                            : { color: BRAND_COLOR, background: '#f5f5ff' }
                        }
                        onClick={() => setSelectedStore(item.store_name)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedStore(item.store_name)}
                      >
                        {item.store_name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {selectedStore && (
              <button
                type="button"
                className="pricing-history__clear-btn"
                style={{ background: '#e7e7fa', color: BRAND_COLOR }}
                onClick={() => setSelectedStore(null)}
              >
                Show All Stores
              </button>
            )}
          </div>
        </section>
        <section className="pricing-history__chart-section">
          {!loading && !error && chartData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={250}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date_bought" angle={-45} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="item_price"
                  stroke={BRAND_COLOR}
                  name="Price"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </section>
      </main>
    </div>
  );
}
